import React, { useState, useEffect, useRef } from "react";
import {
    Stack,
    TextField,
    Switch,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    Box,
    Button,
    Typography,
    IconButton,
    InputAdornment,
    Avatar,
    FormHelperText
} from "@mui/material";
import { Visibility, VisibilityOff, CloudUpload, Person, Delete } from "@mui/icons-material";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import apiService from '@services/common'
import { useGlobalContext } from '@providers/globalProvider';

const AddDrawer = ({ submitFunction = () => { } }) => {
    const [roles, setRoles] = useState([])
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [avatarPreview, setAvatarPreview] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const fileInputRef = useRef(null)

    const { showNotification, showLoading, hideLoading, openModal, closeModal, openDrawer, closeDrawer, showConfirm, closeConfirm } = useGlobalContext()

    useEffect(() => {
        showLoading()
        apiService.get('role')
            .then(res => {
                setRoles(res.data.data)
                hideLoading()
            })
            .catch(err => {
                console.log(err)
                hideLoading()
            })
    }, [])

    const schema = yup.object({
        name: yup.string().required('Vui lòng nhập tên'),
        email: yup.string().email('Email không hợp lệ').required('Vui lòng nhập email'),
        password: yup.string().required('Vui lòng nhập mật khẩu').min(6, 'Mật khẩu ít nhất 6 ký tự'),
        confirm_password: yup.string()
            .oneOf([yup.ref('password')], 'Mật khẩu không khớp')
            .required('Vui lòng xác nhận mật khẩu'),
        role: yup.string(),
    });

    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            role: ''
        }
    });

    // Xử lý khi chọn file
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Kiểm tra loại file
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            showNotification('error', 'Chỉ chấp nhận file ảnh (JPEG, PNG, JPG, GIF, WebP)');
            return;
        }

        // Kiểm tra kích thước (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            showNotification('error', 'Kích thước ảnh tối đa là 2MB');
            return;
        }

        setSelectedFile(file);

        // Tạo preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setAvatarPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // Xóa ảnh đã chọn
    const handleRemoveAvatar = () => {
        setSelectedFile(null);
        setAvatarPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Kích hoạt file input
    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const onSubmit = (data) => {
        // Tạo FormData để gửi cả file
        const formData = new FormData();

        // Thêm các field text
        Object.keys(data).forEach(key => {
            if (key !== 'confirm_password') {
                formData.append(key, data[key]);
            }
        });

        // Thêm file nếu có
        if (selectedFile) {
            formData.append('avatar', selectedFile);
        }

        // Gọi submitFunction với FormData
        submitFunction(formData);
    };

    // Hàm xử lý hiển thị/ẩn mật khẩu
    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    // Ngăn sự kiện mặc định khi click vào icon
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        <Box>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Thêm mới người dùng
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={2}>
                    <Box sx={{ maxWidth: 400 }}>
                        {/* Upload Avatar */}
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept="image/*"
                                style={{ display: 'none' }}
                            />

                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    cursor: 'pointer'
                                }}
                                onClick={handleAvatarClick}
                            >
                                <Avatar
                                    src={avatarPreview}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        border: '2px dashed #ddd',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            opacity: 0.8
                                        }
                                    }}
                                >
                                    <Person sx={{ fontSize: 48, color: '#999' }} />
                                </Avatar>

                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        borderRadius: '50%',
                                        p: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CloudUpload fontSize="small" />
                                </Box>
                            </Box>

                            {avatarPreview && (
                                <Button
                                    size="small"
                                    startIcon={<Delete />}
                                    onClick={handleRemoveAvatar}
                                    sx={{ mt: 1 }}
                                    color="error"
                                >
                                    Xóa ảnh
                                </Button>
                            )}

                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Click để chọn ảnh đại diện (JPEG, PNG, GIF, WebP, max 2MB)
                            </Typography>
                        </Box>

                        {/* Tên */}
                        <TextField
                            label="Tên"
                            fullWidth
                            size="small"
                            {...register('name')}
                            error={!!errors.name}
                            helperText={errors.name?.message}
                        />

                        {/* Email */}
                        <TextField
                            label="Email"
                            fullWidth
                            size="small"
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            sx={{ mt: 2 }}
                        />

                        {/* Role */}
                        <Controller
                            name="role"
                            control={control}
                            render={({ field }) => (
                                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                                    <InputLabel id="role-select-label">Chọn nhóm quyền</InputLabel>
                                    <Select
                                        labelId="role-select-label"
                                        value={field.value}
                                        label="Chọn nhóm quyền"
                                        onChange={field.onChange}
                                    >
                                        <MenuItem value="">Không chọn</MenuItem>
                                        {roles.map((item, index) => (
                                            <MenuItem key={index} value={item.name}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.role && (
                                        <FormHelperText error>{errors.role.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />

                        {/* Mật khẩu */}
                        <TextField
                            label="Mật khẩu"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            size="small"
                            {...register('password')}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            sx={{ mt: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Xác nhận mật khẩu */}
                        <TextField
                            label="Xác nhận mật khẩu"
                            type={showConfirmPassword ? "text" : "password"}
                            fullWidth
                            size="small"
                            {...register('confirm_password')}
                            error={!!errors.confirm_password}
                            helperText={errors.confirm_password?.message}
                            sx={{ mt: 2 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onClick={handleClickShowConfirmPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Nút submit */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="small"
                        >
                            Lưu lại
                        </Button>
                    </Box>
                </Stack>
            </form>
        </Box>
    );
};

export default AddDrawer;